import { IconWithTextComponent, LayoutType, PedagogyUtils } from "@kalyzee/escaet-ui";
import { MeetingSessionOvenMediaReceiver } from "@kalyzee/kast-web-sdk";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { Touchable, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingRoomHighlightingState, MeetingRoomStatus, MeetingSessionHighlightingState, Meeting } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect } from "react";
import { stopRoomHighlight, stopSessionHighlight } from "../../../helpers/meeting";
import { useActivityData } from "../../../hooks/populatedData";
import { ExpertView, PopulatedMeetingData, PopulatedMeetingSession } from "../../../interfaces/populatedData";
import { PedagogyContextState } from "../../../pages/master/pedagogy/context";
import { ExpertsToRemotesContainer } from "../../player/container/ExpertsToRemotesContainer";
import styles from "./highlightContainer.module.css";
import { RestrictedAudioPermission, useMeetingSelector, usePermission } from "../../../hooks/meeting";
import { toastInfo } from "../../../helpers/toast";

export interface PedagogyHighlightContainerProps {
  meetingContainerAPI?: MeetingContainerAPI;
  meeting?: Meeting;
  ongoingGroups?: boolean;
  context: PedagogyContextState | undefined;
  populatedMeetingData?: PopulatedMeetingData;
  highlightView?: ExpertView;
  layoutType: LayoutType;
  disableHeader?: boolean;
  showSelfVideo?: boolean;
  showEnableMicrophone?: boolean;
  onSessionClick?: (session: PopulatedMeetingSession) => void;
  audioValidator?: (session: MeetingSessionOvenMediaReceiver) => boolean;
  style?: React.CSSProperties;
  className?: string;
}
export const PedagogyHighlightContainer = ({
  meetingContainerAPI,
  meeting,
  ongoingGroups,
  context,
  populatedMeetingData,
  highlightView,
  layoutType,
  disableHeader,
  showSelfVideo = true,
  showEnableMicrophone = true,
  onSessionClick,
  audioValidator,
  style,
  className,
}: PedagogyHighlightContainerProps) => {
  const permissionAPI = usePermission(meeting);
  const activityData = useActivityData(populatedMeetingData);
  const contextRef = useRefAutoUpdate(context);

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const onSendBackToSeats = useCallback(async () => {
    if (activityData?.mode !== "highlight") return;

    const groups = populatedMeetingData?.rooms.groups;

    activityData.sessions?.forEach((session) => {
      if (session.session.highlighting === MeetingSessionHighlightingState.ONGOING) {
        stopSessionHighlight(session.session.id);
      }
    });

    // we check groups too
    groups?.forEach((group) => {
      if (group.room.highlighting === MeetingRoomHighlightingState.ONGOING) {
        stopRoomHighlight(group.room.id);
      }
    });
  }, [activityData, populatedMeetingData]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    if (!audioValidator) return;
    const validator = contextRef.current?.addAudioValidator((session) => {
      return audioValidator(session);
    });
    return () => {
      validator?.flush();
    };
  }, [audioValidator]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!highlightView) {
    return (
      <div className={`${styles.container}${className ? ` ${className}` : ""}`} style={{ justifyContent: "center", alignItems: "center" }}>
        Personne au tableau
      </div>
    );
  }

  const renderHeaderHighligt = () => {
    if (disableHeader) return;
    const studentsData = highlightView.students;
    if (!studentsData?.length) return null;

    const groupNamesSet = new Set(
      studentsData.filter((data) => data.video && data.session.user.group?.room.status === MeetingRoomStatus.Ongoing).map((data) => data.session.user?.group?.room.name)
    );
    const groupNames = Array.from(groupNamesSet);

    let isGroupMode = true;
    let message: string;
    if (groupNames.length === 1) {
      message = `Groupe ${groupNames[0]} `;
    } else if (groupNames.length > 1) {
      message = `Groupes ${groupNames.join(", ")} `; //
    } else {
      isGroupMode = false;
      message = "";
    }
    message += "au tableau";

    if (!isGroupMode) return null;

    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "flex-start",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            backgroundColor: "#0090FF",
            padding: "10px 25px 5px 25px",
            borderRadius: "0px 0px 20px 20px",
            color: "white",
            fontWeight: "bold",
          }}
        >
          {message.toUpperCase()}
        </div>
      </div>
    );
  };

  const renderButtons = () => {
    const checked = !permissionAPI.hasPermission("audio", RestrictedAudioPermission);
    return (
      <div style={{
        width: "100%",
        display: "flex",
        alignItems: "stretch",
        justifyContent: "space-between",
        marginTop: 10,
      }}>
        <IconWithTextComponent
          text="Renvoyer à leurs places"
          style={{
            backgroundColor: "white",
            color: "black",
            padding: "1em 2em",
            gap: "0.5em",
            fontSize: "12px",
          }}
          onClick={onSendBackToSeats}
        />
        {showEnableMicrophone && <div className={styles.buttonContainer} onClick={() => {
          if (ongoingGroups) {
            toastInfo("Des groupes sont encore formés. Cette fonctionnalité est accessible uniquement en mode cours.");
          }
        }}>
          <PedagogyUtils.EnableMicrophone
            disabled={ongoingGroups}
            style={{ opacity: ongoingGroups ? 0.4 : 1 }}
            isMicrophoneActive={checked}
            onMicrophoneStatusChange={() => permissionAPI.setPermission("audio", RestrictedAudioPermission, checked)}
          />
        </div>}
      </div>
    );
  };

  return (
    <div className={`${styles.container}${className ? ` ${className}` : ""}`} style={style}>
      {renderHeaderHighligt()}
      <ExpertsToRemotesContainer
        style={{ fontSize: 16, height: "unset", flex: 1, minHeight: 0 }}
        meetingContainerAPI={meetingContainerAPI}
        context={context}
        expertSessions={highlightView.experts.filter((data) => data.video).map((data) => data.session)}
        remoteSessions={highlightView.students.filter((data) => data.video).map((data) => data.session)}
        layout={layoutType}
        showSelf={showSelfVideo ? "current_video" : undefined}
        renderItem={(item, children) => (
          <>
            {children}
            {item.getData().extra?.meetingSession?.user.group?.room.status !== MeetingRoomStatus.Ongoing ? (
              <Touchable
                style={{
                  position: "absolute",
                  color: "white",
                  fontWeight: "bold",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#22222280",
                  top: "7px",
                  right: "7px",
                  cursor: "pointer",
                  zIndex: 1,
                }}
                onPress={(_, event) => {
                  const session = item.getData().extra?.meetingSession;
                  if (session) stopSessionHighlight(session.session.id);
                  event?.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                X
              </Touchable>
            ) : null}
          </>
        )}
        onClickSession={(session) => onSessionClick?.(session)}
      />
      {renderButtons()}
    </div>
  );
};
